import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "../../../components/Dropdown";
import indiaData from "../../../data/stateData.json";
import { Button } from "../../../components/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import Switch from "../../../components/Switch";
import { setStreetFilterData } from "../../../redux/slices/StreetManagement/slice";
import { apiGet } from "../../../services/apiServices";
import { endPoints } from "../../../configs/apiUrls";

/* =====================================================
   Types
===================================================== */
interface FilterState {
  isActive: boolean | null;
  state: string | null;
  city: string | null;
  area: string | null;
}

const initialFilterState: FilterState = {
  isActive: null,
  state: null,
  city: null,
  area: null,
};

/* =====================================================
   Component
===================================================== */
const FilterForm = ({ tableRef }: any) => {
  const dispatch = useDispatch();
  const { filterData } = useSelector((state: RootState) => state.street);

  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [areaOptions, setAreaOptions] = useState<
    { label: string; value: string }[]
  >([]);

  /* ===============================
     Restore filters on open
  =============================== */
  useEffect(() => {
    setFilters({
      isActive: filterData.isActive ?? null,
      state: filterData.state || null,
      city: filterData.city || null,
      area: filterData.area || null,
    });
  }, []);

  /* ===============================
     State Options
  =============================== */
  const stateOptions = useMemo(
    () => indiaData.map((s) => ({ label: s.state, value: s.state })),
    []
  );

  /* ===============================
     City Options (State dependent)
  =============================== */
  const cityOptions = useMemo(() => {
    if (!filters.state) return [];

    const stateObj = indiaData.find((s) => s.state === filters.state);
    if (!stateObj) return [];

    return stateObj.districts.map((d) => ({
      label: d,
      value: d,
    }));
  }, [filters.state]);

  /* ===============================
     Fetch Areas (State + City)
  =============================== */
  useEffect(() => {
    const fetchAreas = async () => {
      if (!filters.state || !filters.city) {
        setAreaOptions([]);
        return;
      }

      try {
        const res = await apiGet(endPoints.GET_AREA_LIST, {
          state: filters.state,
          city: filters.city,
          isActive: "true",
          limit:100000
        });

        setAreaOptions(
          res.data.map((a: any) => ({
            label: a.area,
            value: a._id,
          }))
        );
      } catch {
        setAreaOptions([]);
      }
    };

    fetchAreas();
  }, [filters.state, filters.city]);

  /* ===============================
     Change Handler
  =============================== */
  const onFilterChange = (field: keyof FilterState, value: any) => {
    if (field === "state") {
      setFilters({
        isActive: filters.isActive,
        state: value,
        city: null,
        area: null,
      });
      return;
    }

    if (field === "city") {
      setFilters((prev) => ({
        ...prev,
        city: value,
        area: null,
      }));
      return;
    }

    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /* ===============================
     Apply Filter
  =============================== */
  const handleFilter = () => {
    dispatch(
      setStreetFilterData({
        ...filterData,
        ...filters,
        page: 1,
      })
    );

    tableRef.current?.closeFilterDrawer();
  };

  /* ===============================
     Clear Filter
  =============================== */
  const handleClear = () => {
    dispatch(
      setStreetFilterData({
        ...filterData,
        isActive: null,
        state: "",
        city: "",
        area: "",
        page: 1,
      })
    );

    setFilters(initialFilterState);
    tableRef.current?.closeFilterDrawer();
  };

  /* ===============================
     UI
  =============================== */
  return (
    <div className="h-full flex flex-col p-4">
      <div className="flex-grow overflow-auto space-y-4">

        {/* STATE */}
        <div>
          <label className="block text-sm font-medium mb-1">State</label>
          <Dropdown
            options={stateOptions}
            value={filters.state}
            onChange={(val) => onFilterChange("state", val)}
            placeholder="Select state"
            className="w-full"
          />
        </div>

        {/* CITY */}
        <div>
          <label className="block text-sm font-medium mb-1">City</label>
          <Dropdown
            options={cityOptions}
            value={filters.city}
            onChange={(val) => onFilterChange("city", val)}
            placeholder="Select city"
            disabled={!filters.state}
            className="w-full"
          />
        </div>

        {/* AREA */}
        <div>
          <label className="block text-sm font-medium mb-1">Area</label>
          <Dropdown
            options={areaOptions}
            value={filters.area}
            onChange={(val) => onFilterChange("area", val)}
            placeholder="Select area"
            disabled={!filters.city}
            className="w-full"
          />
        </div>

        {/* ACTIVE */}
        <div>
          <Switch
            checked={filters.isActive ?? true}
            onCheckedChange={(val) => onFilterChange("isActive", val)}
            label="Active"
          />
        </div>
      </div>

      {/* FOOTER */}
      <div className="border-t pt-4 flex justify-end gap-3">
        <Button variant="outline" onClick={handleClear}>
          Clear
        </Button>
        <Button onClick={handleFilter}>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default FilterForm;
